/**
 * Frontend-driven polling helper for long-running Report-typed aggregations
 * (paths, funnels, retention, AI Agent) initiated from a Leo (ask-pendo)
 * conversation.
 *
 * Pattern is modeled on `frontend/main-ui/src/workflows/components/workflow.vue:820-845`
 * but targets the generic Report endpoint (`getReportResource(reportId, '')` →
 * `GET /api/s/_SID_/report/:reportId`), which returns a `ReportRunControlPublic`
 * shape with top-level `currentRunningAgg`, `lastSuccessRunObj`, and
 * `lastFailedRunAt` — distinct from the workflow shape which nests under
 * `data.WorkflowControl`.
 *
 * The helper polls every {@link POLL_INTERVAL_MS} ms and invokes the supplied
 * callbacks on done-success / done-failure transitions. It is intentionally
 * framework-agnostic and unit-testable — Pinia store wiring lives in
 * `src/stores/ask-pendo.store.js`.
 */
import { getReportResource } from '@pendo/agg-connect';

export const POLL_INTERVAL_MS = 5000;

/**
 * Inspect a `ReportRunControlPublic` payload and decide whether the underlying
 * aggregation is done (and how).
 *
 * @param {object} runControl - top-level fields from `GET /api/s/_SID_/report/:id`
 * @returns {{ done: boolean, status: 'success' | 'failed' | 'running' }}
 */
export function classifyReportStatus (runControl) {
    if (!runControl) {
        return { done: false, status: 'running' };
    }

    const { currentRunningAgg, lastSuccessRunObj, lastFailedRunAt } = runControl;

    const isRunning = currentRunningAgg && Object.keys(currentRunningAgg).length > 0;
    if (isRunning) {
        return { done: false, status: 'running' };
    }

    if (lastSuccessRunObj && Object.keys(lastSuccessRunObj).length > 0) {
        return { done: true, status: 'success' };
    }

    if (lastFailedRunAt) {
        return { done: true, status: 'failed' };
    }

    return { done: false, status: 'running' };
}

/**
 * Begin polling a single Report. Returns a `cancel` function that stops the
 * poll loop. On a `success` or `failed` transition the loop self-stops and
 * invokes `onDone({ status, runControl })`. On a 404 (the Report entity is
 * missing / expired) the loop self-stops and invokes `onDone({ status: 'failed', missing: true })`.
 *
 * @param {object} opts
 * @param {string} opts.reportId
 * @param {(result: { status: 'success' | 'failed', missing?: boolean, runControl?: object }) => void} opts.onDone
 * @param {(error: Error) => void} [opts.onError]   transient errors (network, 5xx)
 * @param {() => Promise<unknown>} [opts.fetchReport] override the Report fetch (test seam)
 * @param {number} [opts.intervalMs]
 * @returns {() => void} cancel
 */
export function startPollingReport ({
    reportId,
    onDone,
    onError = () => {},
    fetchReport,
    intervalMs = POLL_INTERVAL_MS,
}) {
    if (!reportId || typeof onDone !== 'function') {
        return () => {};
    }

    let cancelled = false;
    let timerId = null;

    const doFetch = fetchReport
        ? () => fetchReport()
        : () => getReportResource(reportId, '');

    const tick = async () => {
        if (cancelled) return;

        try {
            const response = await doFetch();
            if (cancelled) return;

            const runControl = response?.data || null;
            const { done, status } = classifyReportStatus(runControl);

            if (done) {
                cancelled = true;
                onDone({ status, runControl });

                return;
            }
        } catch (error) {
            if (cancelled) return;

            // 404 means the Report entity is gone (expired or never existed).
            // Treat as terminal failure — caller decides whether to surface a
            // "your analysis has expired" turn or invoke ai-connect synthesis.
            const status = error?.response?.status ?? error?.status;
            if (status === 404) {
                cancelled = true;
                onDone({ status: 'failed', missing: true });

                return;
            }

            onError(error);
        }

        if (cancelled) return;
        timerId = setTimeout(tick, intervalMs);
    };

    // Kick off the first tick on the next macrotask so callers can attach
    // teardown logic before the first network call lands.
    timerId = setTimeout(tick, intervalMs);

    return () => {
        cancelled = true;
        if (timerId !== null) {
            clearTimeout(timerId);
            timerId = null;
        }
    };
}
